import { NavSettingsService } from './services/navSettings';
import { NavSettings } from './types';

const service = NavSettingsService.instance;

const DESKTOPWIDTH = 800;

let navSettings = {
  favorites: [],
  currentlyShowing: null,
  displayScrollBtns: null,
} as NavSettings;

let overlay: HTMLDivElement;
let menus: NodeListOf<HTMLDivElement>;
let subMenu: HTMLDivElement;

let menuMobileOpenBtn: HTMLButtonElement;
let menuMobileCloseBtn: HTMLButtonElement;
let menuToggleBtn: HTMLButtonElement;
let searchInput: HTMLInputElement | null;
let fakeSearchBtn: HTMLButtonElement | null;
let scrollUpBtns: NodeListOf<HTMLButtonElement>;
let scrollDownBtns: NodeListOf<HTMLButtonElement>;

// Multiple client menus because there is one for mobile and one for desktop
let clientMenus: NodeListOf<HTMLDivElement>;

let clientsHideBtns: NodeListOf<HTMLDivElement>;
let clientsShowBtns: NodeListOf<HTMLDivElement>;

let menuItems: NodeListOf<HTMLAnchorElement>;
let navItems: NodeListOf<HTMLAnchorElement>;
let tooltip: HTMLDivElement;
let tooltipHeight: number;

let isLargeMenu: boolean;

const filterItems = (e: Event) => {
  const field = e.target as HTMLInputElement;
  const value = field.value;
  const term = value.trim().toLowerCase();

  // Filter menu items
  menuItems.forEach((item) => {
    if (item.innerText.toLowerCase().includes(term)) {
      item.classList.remove('d-none');
      item.classList.add('d-flex');
    } else {
      item.classList.remove('d-flex');
      item.classList.add('d-none');
    }
  });

  // Filter clients
  if (subMenu) {
    const isDesktop = window.innerWidth >= DESKTOPWIDTH;

    clientMenus.forEach((cm) => {
      // Gather the accordions
      const accordions = [...cm.querySelectorAll('[data-client-accordion]')] as HTMLDivElement[];

      // Iterate through each accordion
      accordions.forEach((acc) => {
        const clientName = acc.dataset.clientAccordion;
        const accIsMobile = acc.classList.contains('mobile');
        if (clientName.toLowerCase().includes(term)) {
          if (isDesktop && !accIsMobile) acc.style.display = 'block';
          if (!isDesktop && accIsMobile) acc.style.display = 'block';
        } else {
          if (isDesktop && !accIsMobile) acc.style.display = 'none';
          if (!isDesktop && accIsMobile) acc.style.display = 'none';
        }
      });
    });
  }
};

const expandMenu = () => {
  menus.forEach((menu) => menu.classList.remove('menu-collapse'));
  subMenu?.classList.add('move-submenu');
  overlay.classList.add('overlay-active');

  const btnsExpanded = document.querySelectorAll('[data-btn-client-expanded]') as NodeListOf<HTMLDivElement>;
  const btnsCollapsed = document.querySelectorAll('[data-btn-client-collapsed]') as NodeListOf<HTMLDivElement>;

  btnsExpanded.forEach((btn) => {
    btn.classList.remove('hide');
  });
  btnsCollapsed.forEach((btn) => {
    btn.classList.add('hide');
  });

  isLargeMenu = true;
};

const collapseMenu = () => {
  menus.forEach((menu) => menu.classList.add('menu-collapse'));
  subMenu?.classList.remove('move-submenu');
  overlay.classList.remove('overlay-active');

  const btnsExpanded = document.querySelectorAll('[data-btn-client-expanded]') as NodeListOf<HTMLDivElement>;
  const btnsCollapsed = document.querySelectorAll('[data-btn-client-collapsed]') as NodeListOf<HTMLDivElement>;

  btnsExpanded.forEach((btn) => {
    btn.classList.add('hide');
  });
  btnsCollapsed.forEach((btn) => {
    btn.classList.remove('hide');
  });

  isLargeMenu = false;
};

const toggleMenu = () => {
  if (isLargeMenu) {
    collapseMenu();
  } else {
    expandMenu();
  }
};

const scrollMenu = (btn: HTMLButtonElement, direction: number) => {
  const navItemHeight = 40; // Defined in menu.scss

  const scrollArea = btn.parentElement.querySelector('[data-scrollable]');
  scrollArea.scrollTop = scrollArea.scrollTop += navItemHeight * 5 * direction;
};

const hideClients = async (e: Event) => {
  const btn = e.target as HTMLButtonElement;
  const parent = btn.parentElement;
  const accordionName = parent.dataset.clientAccordion;

  const isDesktop = window.innerWidth >= DESKTOPWIDTH;

  // Get all of the accordions
  const accordions = document.querySelectorAll('[data-client-accordion]') as NodeListOf<HTMLDivElement>;

  accordions.forEach((acc) => {
    // Hide the hide button
    (acc.querySelector('[data-hide-others]') as HTMLButtonElement).style.display = 'none';

    const accIsMobile = acc.classList.contains('mobile');

    // Display the show button
    const showBtn = acc.querySelector('[data-show-others]') as HTMLButtonElement;
    if (showBtn) showBtn.style.display = 'flex';

    if (acc.dataset.clientAccordion !== accordionName) {
      // Hides correct accordions (desktop or mobile)
      if (isDesktop && !accIsMobile) acc.style.display = 'none';
      if (!isDesktop && accIsMobile) acc.style.display = 'none';
    }
  });

  // Finally, call the api
  try {
    navSettings.currentlyShowing = accordionName;
    await service.updateNavSettings(navSettings);
  } catch (error) {
    console.error(error);
  }
};

const showClients = async (e: Event) => {
  const isDesktop = window.innerWidth >= DESKTOPWIDTH;

  // Get all of the accordions
  const accordions = document.querySelectorAll('[data-client-accordion]') as NodeListOf<HTMLDivElement>;

  accordions.forEach((acc) => {
    // Hide the show button
    (acc.querySelector('[data-show-others]') as HTMLButtonElement).style.display = 'none';

    // Display the hide button
    const hideBtn = acc.querySelector('[data-hide-others]') as HTMLButtonElement;
    if (hideBtn) hideBtn.style.display = 'flex';

    const accIsMobile = acc.classList.contains('mobile');

    // Hides correct accordions (desktop or mobile)
    if (isDesktop && !accIsMobile) acc.style.display = 'block';
    if (!isDesktop && accIsMobile) acc.style.display = 'block';
  });

  // Finally, call the api
  try {
    navSettings.currentlyShowing = null;
    await service.updateNavSettings(navSettings);
  } catch (error) {
    console.error(error);
  }
};

const toggleFavorite = async (e: Event) => {
  e.preventDefault();
  e.stopImmediatePropagation();

  let navFavorites = navSettings.favorites;
  if (!navFavorites) {
    navFavorites = [];
    navSettings.favorites = navFavorites;
  }

  const favBtn = e.target as HTMLButtonElement;
  const item = favBtn.parentElement.parentElement as HTMLAnchorElement;
  const itemName = item.dataset.tooltipName;
  const itemIndex = navFavorites.findIndex((fav) => fav === itemName);

  if (itemIndex !== -1) {
    navFavorites.splice(itemIndex, 1);
    moveItem(itemName, false);
  } else {
    navFavorites.push(itemName);
    moveItem(itemName, true);
  }

  try {
    navFavorites.sort((a, b) => a.localeCompare(b));

    navSettings.favorites = navFavorites;
    await service.updateNavSettings(navSettings);
  } catch (error) {
    console.error(error);
  }
};

const changeIconType = (item: HTMLAnchorElement, type: string) => {
  const icon = item.querySelector('.favorite-button > .fa');

  icon.classList.remove(type === 'solid' ? 'fa-regular' : 'fa-solid');
  icon.classList.add(type === 'solid' ? 'fa-solid' : 'fa-regular');
};

const sortSectionItems = (section: HTMLDivElement) => {
  // First, sort alphabetically
  const itemsInSection = [...section.querySelectorAll('[data-nav-item]')] as HTMLAnchorElement[];

  itemsInSection.sort((a, b) => {
    return a.dataset.tooltipName.localeCompare(b.dataset.tooltipName);
  });

  // Finally, we actually place the items where they go
  itemsInSection.forEach((i) => section.appendChild(i));
};

const moveItem = (itemName: string, isFavorite: boolean) => {
  const menuAreas = [...document.querySelectorAll('[data-menu-sections]')] as HTMLDivElement[];

  if (isFavorite) {
    // Each menu area has sections
    menuAreas.forEach((area) => {
      // In each area, find the favorites container
      const favoritesContainer = area.querySelector('[data-favorites-container]') as HTMLDivElement;

      // Get its favorites section
      const favoritesSection = favoritesContainer.querySelector('[data-section="favorites"]') as HTMLDivElement;

      // Get all items in section
      const items = [...area.querySelectorAll('[data-nav-item]')] as HTMLAnchorElement[];

      // Find the item we're concerned with
      const item = items.find((it) => it.dataset.tooltipName === itemName);

      // Move to favorites area
      if (item) {
        favoritesSection.appendChild(item);
        changeIconType(item, 'solid');
        sortSectionItems(favoritesSection);
        favoritesContainer.style.display = 'block';
      }
    });
  } else {
    // Each menu area has sections
    menuAreas.forEach((area) => {
      // In each area, find the favorites container
      const favoritesContainer = area.querySelector('[data-favorites-container]') as HTMLDivElement;

      // Get its favorites section
      const favoritesSection = favoritesContainer.querySelector('[data-section="favorites"]') as HTMLDivElement;

      // Get all items in section
      const items = [...area.querySelectorAll('[data-nav-item]')] as HTMLAnchorElement[];

      // Find the item we're concerned with
      const item = items.find((it) => it.dataset.tooltipName === itemName);

      if (item) {
        // Find out where it wants to go
        const sectionName = item.dataset.navSection;

        // Get that section
        const section = area.querySelector(`[data-section="${sectionName}"]`) as HTMLDivElement;

        // Make the move
        section.appendChild(item);
        changeIconType(item, 'regular');
        sortSectionItems(section);
      }

      if (favoritesSection.childElementCount === 0) {
        favoritesContainer.style.display = 'none';
      }
    });
  }
};

const initFavorites = () => {
  const navFavorites = navSettings.favorites;

  const menuAreas = [...document.querySelectorAll('[data-menu-sections]')] as HTMLDivElement[];

  // Each menu area has sections
  menuAreas.forEach((area) => {
    // In each area, find the favorites container
    const favoritesContainer = area.querySelector('[data-favorites-container]') as HTMLDivElement;

    // Get its links section
    const favoritesSection = favoritesContainer.querySelector('[data-section="favorites"]') as HTMLDivElement;

    // Get all items in this area
    const items = [...area.querySelectorAll('[data-nav-item]')] as HTMLAnchorElement[];

    // For each favorite, check the items to see if it's found and move to favorites section
    navFavorites?.forEach((fav) => {
      for (let i = 0; i < items.length; i++) {
        if (fav === items[i].dataset.tooltipName) {
          changeIconType(items[i], 'solid');
          favoritesSection.appendChild(items[i]);
          favoritesContainer.style.display = 'block';
          break;
        }
      }
    });
  });
};

const initClients = async () => {
  const currentlyShowing = navSettings.currentlyShowing;
  if (!currentlyShowing) return;

  // Get all of the accordions
  const accordions = document.querySelectorAll('[data-client-accordion]') as NodeListOf<HTMLDivElement>;

  // Check if at least one accordion matches currentlyShowing
  let found = false;
  for (let i = 0; i < accordions.length; i++) {
    const acc = accordions[i];
    if (acc.dataset.clientAccordion === currentlyShowing) {
      found = true;
      break;
    }
  }

  if (found) {
    accordions?.forEach((acc) => {
      if (acc.dataset.clientAccordion !== currentlyShowing) {
        // If the current accordion's data-client-accordion does not match current, hide
        acc.style.display = 'none';
      } else {
        acc.classList.add('show');

        // For the current one, hide the hide button, and display the show button
        (acc.querySelector('[data-hide-others]') as HTMLButtonElement).style.display = 'none';
        const showBtn = acc.querySelector('[data-show-others]') as HTMLButtonElement;
        if (showBtn) showBtn.style.display = 'flex';
      }
    });
  } else {
    // Something went wrong and currentlyShowing doesn't match any company...reset it
    try {
      navSettings.currentlyShowing = '';
      await service.updateNavSettings(navSettings);
    } catch (error) {
      console.error(error);
    }
  }
};

const init = () => {
  overlay = document.querySelector('[data-overlay]');
  menus = document.querySelectorAll('[data-menu]');
  subMenu = document.querySelector('[data-submenu]');
  menuMobileOpenBtn = document.querySelector('[data-menu-toggle-button-open]');
  menuMobileCloseBtn = document.querySelector('[data-menu-toggle-button-close]');
  menuToggleBtn = document.querySelector('[data-menu-toggle-button]');
  searchInput = document.querySelector('[data-menu-search-input]');
  fakeSearchBtn = document.querySelector('[data-menu-search-fake-button]');
  clientMenus = document.querySelectorAll('[data-client-menu]');
  clientsHideBtns = document.querySelectorAll('[data-hide-others]');
  clientsShowBtns = document.querySelectorAll('[data-show-others]');
  scrollUpBtns = document.querySelectorAll('[data-btn-scroll-up]');
  scrollDownBtns = document.querySelectorAll('[data-btn-scroll-down]');

  menuItems = document.querySelectorAll('.nav-item');
  navItems = document.querySelectorAll('[data-nav-item]');
  tooltip = document.querySelector('[data-menu-tooltip]');
  tooltipHeight = tooltip?.getBoundingClientRect()?.height || 0;

  isLargeMenu = menus?.length ? !menus[0].classList.contains('menu-collapse') : false;

  // Sort all menu items in all menu areas
  const menuAreas = [...document.querySelectorAll('[data-menu-sections]')] as HTMLDivElement[];
  menuAreas.forEach((area) => {
    // Each area has sections
    const sections = area.querySelectorAll(`[data-section]`) as NodeListOf<HTMLDivElement>;

    sections.forEach(function (section: HTMLDivElement) {
      if (section.dataset.section !== 'user') {
        // Don't sort the users section (we want the order that's there)
        sortSectionItems(section);
      }
    });
  });

  //  Gather user nav favorites
  const settings = menus[0]?.dataset.navSettings as string;
  if (settings && settings !== 'null' && settings !== 'undefined') {
    navSettings = { ...JSON.parse(settings) };
  }

  initFavorites();
  initClients();

  overlay?.addEventListener('click', () => {
    overlay.classList.remove('overlay-active');
    toggleMenu();
  });

  menuMobileOpenBtn?.addEventListener('click', expandMenu);
  menuMobileCloseBtn?.addEventListener('click', collapseMenu);
  menuToggleBtn?.addEventListener('click', toggleMenu);
  searchInput?.addEventListener('keyup', (e) => filterItems(e));
  searchInput?.addEventListener('search', (e) => filterItems(e));
  fakeSearchBtn?.addEventListener('click', () => {
    toggleMenu();
    searchInput.focus();
  });

  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (menus[0]?.classList?.contains('menu-collapse')) return;
    if (e.code === 'Escape') collapseMenu();
  });

  scrollUpBtns?.forEach((btn) => {
    btn.addEventListener('click', () => scrollMenu(btn, 1));
  });
  scrollDownBtns?.forEach((btn) => {
    btn.addEventListener('click', () => scrollMenu(btn, -1));
  });

  menuItems.forEach((item) => {
    item.addEventListener('mouseover', () => {
      if (!menus[0].classList.contains('menu-collapse')) return;

      const xPadding = 10;

      const { y: itemY, height: itemH, right: itemR } = item.getBoundingClientRect();

      tooltip.style.display = 'block';
      tooltip.innerText = item.dataset.tooltipName;
      tooltip.style.top = itemY - tooltipHeight * 0.5 + window.scrollY + 'px';
      tooltip.style.left = itemR + xPadding + 'px';
    });

    item.addEventListener('mouseleave', () => {
      if (!menus[0].classList.contains('menu-collapse')) return;
      tooltip.style.display = 'none';
    });
  });

  // Favorite buttons
  navItems.forEach((item) => {
    const favBtn = item.querySelector('[data-favorite-btn]') as HTMLButtonElement;
    favBtn?.addEventListener('click', toggleFavorite);
  });

  // Click events for company menu toggles
  clientMenus.forEach((cm) => {
    // Find the toggle within this section
    const btnExpanded = cm.querySelectorAll('[data-btn-client-expanded]') as NodeListOf<HTMLButtonElement>;
    const btnCollapsed = cm.querySelectorAll('[data-btn-client-collapsed]') as NodeListOf<HTMLButtonElement>;

    if (isLargeMenu) {
      btnCollapsed?.forEach((btn) => btn.classList.add('hide'));
      btnExpanded.forEach((btn) => btn.classList.remove('hide'));
    } else {
      btnCollapsed?.forEach((btn) => btn.classList.remove('hide'));
      btnExpanded.forEach((btn) => btn.classList.add('hide'));
    }

    btnExpanded.forEach((btn) =>
      btn.addEventListener('click', () => {
        const parentAccordion = btn.parentElement;
        parentAccordion.classList.toggle('show');
      })
    );
    btnCollapsed?.forEach((btn) =>
      btn.addEventListener('click', () => {
        const parentAccordion = btn.parentElement;
        parentAccordion.classList.toggle('show');
      })
    );
  });

  // Hide clients other than this one
  [...clientsHideBtns].forEach((b) => {
    b.addEventListener('click', hideClients);
  });

  // Show all clients
  [...clientsShowBtns].forEach((b) => {
    b.addEventListener('click', showClients);
  });

  // Intercom - open with link
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('intercom')) {
    // Get the secdesk button and click
    const intercomBtn = document.querySelector('.intercom-launcher') as HTMLButtonElement;
    intercomBtn?.click();
  }
};

init();
